import { SETTINGS_SECTIONS } from '../../app/terminology/settings';
import {
    Badge,
    Banner,
    BlockStack,
    Box,
    Button,
    Card,
    Checkbox,
    Icon,
    InlineStack,
    Layout,
    SkeletonBodyText,
    Text,
    TextField,
} from "@shopify/polaris";
import { SafeSaveBar as SaveBar } from "../../app/components/SafeAppBridge";
import { ArrowDownIcon, ArrowUpIcon, CheckIcon, DragHandleIcon, InfoIcon, PlusIcon, SortIcon } from "@shopify/polaris-icons";
import {
    type PointerEvent as ReactPointerEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import agent from "../../app/api/agent";
import type {
    FitmentCatalogSettings,
    FitmentFieldDataType,
    FitmentFieldDefinition,
    FitmentFieldSortDirection,
    FitmentFieldSortMode,
} from "../../app/models/FitmentCatalog";
import { setCachedFitmentCatalogLabel } from "../../app/utils/fitmentCatalogLabel";
import { SettingsTooltip } from "./SettingsTooltip";
import Turn14PartnerCallout from "./Turn14PartnerCallout";
import Turn14IntegrationSettings from "./Turn14IntegrationSettings";
import type { Turn14IntegrationSettings as Turn14IntegrationSettingsModel } from "../../app/models/Turn14Integration";
import FitmentFieldEditor from "./FitmentFieldEditor";
import "./FitmentFieldsSettings.css";

const fieldRoleOptionGroups = [
    {
        title: "General",
        options: [
            { label: "Custom", value: "custom" },
        ],
    },
    {
        title: "Standard fitment fields",
        options: [
            { label: "Year", value: "year" },
            { label: "Make", value: "make" },
            { label: "Model", value: "model" },
            { label: "Submodel / Trim", value: "submodel" },
            { label: "Engine", value: "engine" },
            { label: "Displacement", value: "displacement" },
            { label: "Body / Vehicle Type", value: "body_vehicle_type" },
            { label: "Drive Type", value: "drive_type" },
            { label: "Transmission", value: "transmission" },
            { label: "Fuel Type", value: "fuel_type" },
            { label: "Cab Type", value: "cab_type" },
            { label: "Bed Length", value: "bed_length" },
            { label: "Wheelbase", value: "wheelbase" },
            { label: "Doors", value: "doors" },
            { label: "Brake Type", value: "brake_type" },
            { label: "Brake Size", value: "brake_size" },
            { label: "Tire Size", value: "tire_size" },
            { label: "Wheel Size", value: "wheel_size" },
            { label: "Chain Size", value: "chain_size" },
            { label: "Region / Market", value: "region_market" },
            { label: "Chassis Code", value: "chassis_code" },
            { label: "Production Date", value: "production_date" },
            { label: "Position", value: "position" },
            { label: "Option / Package", value: "option_package" },
        ],
    },
];

export type FitmentFieldsEditorStatus = {
    draft?: FitmentCatalogSettings;
    baseline?: FitmentCatalogSettings;
    error?: string;
    reload?: () => void;
    hasChanges: boolean;
    loading: boolean;
    saving: boolean;
    save?: () => Promise<void>;
    discard?: () => void;
};

export type FitmentFieldsSettingsSection = "fields" | "referenceData";

type FitmentFieldsSettingsProps = {
    onStatusChange?: (status: FitmentFieldsEditorStatus) => void;
    onOpenFitmentSpreadsheet?: () => void;
    autoOpenTurn14?: boolean;
    onTurn14AutoOpened?: () => void;
    section?: FitmentFieldsSettingsSection;
    showSaveBar?: boolean;
};

type FieldDragState = {
    sourceIndex: number;
    destinationIndex: number;
    pointerX: number;
    pointerY: number;
    offsetX: number;
    offsetY: number;
    width: number;
    portalContainer: HTMLElement;
};

type FieldDragStartEvent = ReactPointerEvent<HTMLElement>;

const standardVehicleSupportedFields = "Year, make, model, and submodel/trim, plus engine, displacement, body type, drive type, transmission, fuel type, doors, bed length, wheelbase, brake type, chassis code, and region/market.";
const turn14ProductLinkBehavior = "Map your Shopify SKUs to Turn14 products for fitment matching.";
const turn14ShopifyTagBehavior = "Add Turn14 vehicle fitments to your products as Shopify tags.";
const turn14TagLimitBehavior = "Products that would exceed Shopify's 250-tag limit are skipped instead of receiving an incomplete set of vehicle tags.";

const ReferenceSourceSwitch = ({ label, checked, disabled, onChange }: {
    label: string;
    checked: boolean;
    disabled: boolean;
    onChange: (checked: boolean) => void;
}) => (
    <button
        type="button"
        role="switch"
        className="pf-fitment-source__switch"
        aria-label={label}
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
    >
        <span aria-hidden="true" />
    </button>
);

const standardFieldDefaults: Record<string, { index: number; label: string; required: boolean }> = {
    year: { index: 1, label: "Year", required: true },
    make: { index: 2, label: "Make", required: true },
    model: { index: 3, label: "Model", required: true },
    submodel: { index: 4, label: "Submodel / Trim", required: false },
    engine: { index: 5, label: "Engine", required: false },
    displacement: { index: 6, label: "Displacement", required: false },
    body_vehicle_type: { index: 7, label: "Body / Vehicle Type", required: false },
    drive_type: { index: 8, label: "Drive Type", required: false },
    transmission: { index: 9, label: "Transmission", required: false },
    fuel_type: { index: 10, label: "Fuel Type", required: false },
    cab_type: { index: 11, label: "Cab Type", required: false },
    bed_length: { index: 12, label: "Bed Length", required: false },
    wheelbase: { index: 13, label: "Wheelbase", required: false },
    doors: { index: 14, label: "Doors", required: false },
    brake_type: { index: 15, label: "Brake Type", required: false },
    brake_size: { index: 16, label: "Brake Size", required: false },
    tire_size: { index: 17, label: "Tire Size", required: false },
    wheel_size: { index: 18, label: "Wheel Size", required: false },
    chain_size: { index: 19, label: "Chain Size", required: false },
    region_market: { index: 20, label: "Region / Market", required: false },
    chassis_code: { index: 21, label: "Chassis Code", required: false },
    production_date: { index: 22, label: "Production Date", required: false },
    position: { index: 23, label: "Position", required: false },
    option_package: { index: 24, label: "Option / Package", required: false },
};

const standardFieldKeys = new Set(Object.keys(standardFieldDefaults));
const requiredStandardFieldKeys = new Set(["year", "make", "model"]);
const standardCompatibilityOrders = [
    ["year", "make", "model"],
    ["make", "model", "year"],
];
const defaultTextMaxLength = 200;

const cloneCatalog = (catalog: FitmentCatalogSettings): FitmentCatalogSettings => ({
    ...catalog,
    fields: catalog.fields.map((field) => ({ ...field })),
});

function getReorderedPreview<T>(items: T[], sourceIndex: number, destinationIndex: number) {
    if (sourceIndex === destinationIndex) return items;

    const next = [...items];
    const [movedItem] = next.splice(sourceIndex, 1);
    next.splice(destinationIndex, 0, movedItem);
    return next;
}

const normalizeDisplayOrder = (fields: FitmentFieldDefinition[]) =>
    fields.map((field, index) => ({ ...field, displayOrder: index + 1 }));

const normalizeKey = (value: string) =>
    value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

const inferCatalogType = (fields: FitmentFieldDefinition[]) => {
    const enabledKeys = new Set(fields.filter((field) => field.isEnabled).map((field) => normalizeKey(field.key || field.label)));
    return ["year", "make", "model"].every((key) => enabledKeys.has(key)) ? "Hybrid" : "Custom";
};

const getFieldRole = (field: FitmentFieldDefinition) => {
    const key = normalizeKey(field.key || field.label);
    return standardFieldKeys.has(key) ? key : "custom";
};

const isRequiredLockedField = (field: FitmentFieldDefinition) =>
    requiredStandardFieldKeys.has(getFieldRole(field));

const getDefaultSortDirection = (roleOrField: string | FitmentFieldDefinition): FitmentFieldSortDirection =>
    (typeof roleOrField === "string" ? roleOrField : getFieldRole(roleOrField)) === "year" ? "Desc" : "Asc";

const getDefaultSortMode = (): FitmentFieldSortMode => "NaturalNumeric";

const getDefaultRuleState = (role: string, hasStandardFitmentSetup: boolean) => {
    if (requiredStandardFieldKeys.has(role)) {
        return { isRequired: true, isFilterable: true };
    }

    if (role === "custom" && !hasStandardFitmentSetup) {
        return { isRequired: true, isFilterable: true };
    }

    return { isRequired: false, isFilterable: false };
};

const normalizeSimpleFieldSettings = (field: FitmentFieldDefinition): FitmentFieldDefinition => {
    const requiredLocked = isRequiredLockedField(field);
    const isRequired = field.isEnabled ? requiredLocked || field.isRequired : false;

    return {
        ...field,
        isRequired,
        isFilterable: field.isEnabled && isRequired && (requiredLocked || field.isFilterable),
        sortDirection: field.sortDirection ?? getDefaultSortDirection(field),
        sortMode: field.sortMode ?? getDefaultSortMode(),
        maxLength: field.dataType === "Text" ? defaultTextMaxLength : null,
    };
};

const normalizeSimpleCatalogSettings = (catalog: FitmentCatalogSettings): FitmentCatalogSettings => ({
    ...catalog,
    includeVcdbReferenceData: catalog.includeVcdbReferenceData ?? false,
    includeTurn14ReferenceData: catalog.includeTurn14ReferenceData ?? false,
    syncTurn14ProductLinks: catalog.syncTurn14ProductLinks ?? true,
    syncTurn14VehicleTagsToShopify: catalog.syncTurn14VehicleTagsToShopify ?? false,
    skipShopifyVehicleTagSyncWhenOverLimit: catalog.skipShopifyVehicleTagSyncWhenOverLimit ?? true,
    turn14ReferenceDataAvailable: catalog.turn14ReferenceDataAvailable ?? false,
    fields: catalog.fields.map(normalizeSimpleFieldSettings),
});

const getFieldRoleOptions = (fields: FitmentFieldDefinition[], currentIndex: number) => {
    const usedStandardKeys = new Set(
        fields
            .filter((field, index) => index !== currentIndex && field.isEnabled)
            .map((field) => normalizeKey(field.key || field.label))
            .filter((key) => standardFieldKeys.has(key))
    );

    return fieldRoleOptionGroups.map((group) => ({
        ...group,
        options: group.options.map((option) => ({
            ...option,
            disabled: option.value !== "custom" && usedStandardKeys.has(option.value),
        })),
    }));
};

const createBlankField = (displayOrder: number): FitmentFieldDefinition => ({
    fieldIndex: 0,
    key: "",
    label: "",
    dataType: "Text",
    isEnabled: true,
    isRequired: false,
    isFilterable: false,
    isVisible: true,
    displayOrder,
    maxLength: defaultTextMaxLength,
    sortDirection: "Asc",
    sortMode: getDefaultSortMode(),
    placeholder: "",
    helpText: "",
});

const createFieldFromRole = (displayOrder: number, role: string, hasStandardFitmentSetup = false): FitmentFieldDefinition => {
    const blankField = createBlankField(displayOrder);
    const defaults = standardFieldDefaults[role];
    const ruleState = getDefaultRuleState(role, hasStandardFitmentSetup);

    if (!defaults) {
        return normalizeSimpleFieldSettings({
            ...blankField,
            ...ruleState,
        });
    }

    return normalizeSimpleFieldSettings({
        ...blankField,
        key: role,
        label: defaults.label,
        ...ruleState,
        sortDirection: getDefaultSortDirection(role),
    });
};

const getActiveCatalog = (catalog: FitmentCatalogSettings): FitmentCatalogSettings => ({
    ...catalog,
    fields: normalizeDisplayOrder(catalog.fields.filter((field) => field.isEnabled)),
});

const isRoleAlreadyUsed = (fields: FitmentFieldDefinition[], role: string, currentIndex = -1) =>
    role !== "custom" && fields.some((field, index) =>
        index !== currentIndex && field.isEnabled && getFieldRole(field) === role
    );

const getStandardFitmentCompatibility = (fields: FitmentFieldDefinition[]) => {
    const activeRoles = fields.filter((field) => field.isEnabled).map(getFieldRole);
    const activeByRole = new Map(
        fields
            .filter((field) => field.isEnabled)
            .map((field) => [getFieldRole(field), field])
    );
    const issues: string[] = [];
    const hasStandardOrder = standardCompatibilityOrders.some((order) =>
        order.every((role, index) => activeRoles[index] === role)
    );

    if (!hasStandardOrder) {
        issues.push("Year, Make, and Model must be the first fields in Year-Make-Model or Make-Model-Year order");
    }

    requiredStandardFieldKeys.forEach((role) => {
        const label = standardFieldDefaults[role].label;

        if (!activeByRole.has(role)) {
            issues.push(`${label} field type is missing`);
            return;
        }

        if (!activeByRole.get(role)?.isRequired) {
            issues.push(`${label} must remain required`);
        }
    });

    const compatible = issues.length === 0;

    return {
        compatible,
        label: compatible ? "Standard fitment" : "Custom setup",
        tone: compatible ? "success" as const : undefined,
        tooltip: compatible
            ? "Year, Make, and Model are active and mapped as the first fields in a standard order. This keeps ACES/PIES-style imports, Turn14 fitment, and similar fitment standards available."
            : "This setup can be used for custom selectors. Add Year, Make, and Model field types to enable standard fitment compatibility.",
    };
};

const FitmentFieldsSettings: React.FC<FitmentFieldsSettingsProps> = ({
    onStatusChange,
    onOpenFitmentSpreadsheet,
    autoOpenTurn14 = false,
    onTurn14AutoOpened,
    section = "fields",
    showSaveBar = true,
}) => {
    const [draft, setDraft] = useState<FitmentCatalogSettings | undefined>();
    const [baseline, setBaseline] = useState<FitmentCatalogSettings | undefined>();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [reordering, setReordering] = useState(false);
    const [compatibilityInfoOpen, setCompatibilityInfoOpen] = useState(false);
    const [reorderMessage, setReorderMessage] = useState("");
    const [editingFieldIndex, setEditingFieldIndex] = useState<number | undefined>();
    const [fieldDrag, setFieldDrag] = useState<FieldDragState | undefined>();
    const activeFieldsRef = useRef<{ field: FitmentFieldDefinition; originalIndex: number }[]>([]);
    const fieldDragRef = useRef<FieldDragState | undefined>();
    const fieldRowRefs = useRef(new Map<number, HTMLDivElement>());
    const dragCleanupRef = useRef<(() => void) | undefined>();
    const [error, setError] = useState<string | undefined>();
    const [success, setSuccess] = useState<string | undefined>();

    useEffect(() => {
        if (!success || section !== "fields") return;
        const timeout = window.setTimeout(() => setSuccess(undefined), 5000);
        return () => window.clearTimeout(timeout);
    }, [success, section]);

    const [loadAttempt, setLoadAttempt] = useState(0);
    const reload = useCallback(() => setLoadAttempt(attempt => attempt + 1), []);

    useEffect(() => {
        const loadCatalog = async () => {
            setLoading(true);
            setError(undefined);

            try {
                const catalog = await agent.Settings.fitmentCatalog();
                const normalizedCatalog = {
                    ...catalog,
                    name: catalog.name === "Default fitment catalog" ? "Vehicles" : catalog.name,
                };
                const simpleCatalog = getActiveCatalog(normalizeSimpleCatalogSettings(normalizedCatalog));
                setCachedFitmentCatalogLabel(simpleCatalog.name);
                setDraft(cloneCatalog(simpleCatalog));
                setBaseline(cloneCatalog(simpleCatalog));
                setEditingFieldIndex(undefined);
            } catch (err: any) {
                setError(err?.message ?? "Failed to load fitment fields.");
            } finally {
                setLoading(false);
            }
        };

        loadCatalog();
    }, [loadAttempt]);

    const hasUnsavedChanges = useMemo(
        () => !!draft && !!baseline && JSON.stringify(draft) !== JSON.stringify(baseline),
        [draft, baseline]
    );

    const indexedFields = useMemo(
        () => draft?.fields.map((field, originalIndex) => ({ field, originalIndex })) ?? [],
        [draft]
    );

    const activeFields = useMemo(
        () => indexedFields.filter(({ field }) => field.isEnabled),
        [indexedFields]
    );
    const displayedActiveFields = useMemo(
        () => fieldDrag
            ? getReorderedPreview(activeFields, fieldDrag.sourceIndex, fieldDrag.destinationIndex)
            : activeFields,
        [activeFields, fieldDrag]
    );
    const draggedActiveField = fieldDrag ? activeFields[fieldDrag.sourceIndex] : undefined;

    useEffect(() => {
        activeFieldsRef.current = activeFields;
    }, [activeFields]);

    useEffect(() => () => {
        dragCleanupRef.current?.();
    }, []);

    const enabledFieldCount = activeFields.length;
    const standardFitmentCompatibility = useMemo(
        () => getStandardFitmentCompatibility(draft?.fields ?? []),
        [draft?.fields]
    );
    const selectedField = editingFieldIndex === undefined ? undefined : draft?.fields[editingFieldIndex];
    const canOpenAddField = !!draft && enabledFieldCount < draft.maxFields;

    const updateCatalog = useCallback((changes: Partial<FitmentCatalogSettings>) => {
        setSuccess(undefined);
        setDraft((current) => current ? ({ ...current, ...changes }) : current);
    }, []);

    const handleTurn14Saved = useCallback((settings: Turn14IntegrationSettingsModel) => {
        // The connection saves these values immediately. Preserve the rest of
        // the page draft so connecting never discards unrelated pending edits.
        const applyConnection = (current: FitmentCatalogSettings | undefined) => current ? ({
            ...current,
            turn14ReferenceDataAvailable: settings.fitmentDataAvailable,
            includeTurn14ReferenceData: settings.fitmentDataEnabled,
        }) : current;
        setDraft(applyConnection);
        setBaseline(applyConnection);
        setSuccess("Turn14 integration settings saved.");
    }, []);

    const updateField = useCallback((index: number, changes: Partial<FitmentFieldDefinition>) => {
        setSuccess(undefined);
        setDraft((current) => {
            if (!current) return current;

            const fields = current.fields.map((field, fieldIndex) =>
                fieldIndex === index ? normalizeSimpleFieldSettings({ ...field, ...changes }) : field
            );

            return { ...current, catalogType: inferCatalogType(fields), fields };
        });
    }, []);

    const updateFieldRole = useCallback((index: number, role: string) => {
        setSuccess(undefined);
        setDraft((current) => {
            if (!current) return current;

            if (isRoleAlreadyUsed(current.fields, role, index)) return current;

            const fields = current.fields.map((field, fieldIndex) => {
                if (fieldIndex !== index) return field;

                const currentRole = getFieldRole(field);
                if (role === "custom") {
                    const ruleState = currentRole === "custom"
                        ? { isRequired: field.isRequired, isFilterable: field.isFilterable }
                        : getDefaultRuleState(role, getStandardFitmentCompatibility(current.fields).compatible);

                    return normalizeSimpleFieldSettings({
                        ...field,
                        key: currentRole === "custom" ? field.key : `custom_${field.fieldIndex || index + 1}`,
                        dataType: field.dataType || "Text",
                        ...ruleState,
                        sortDirection: currentRole === "custom" ? field.sortDirection : getDefaultSortDirection(role),
                    });
                }

                const defaults = standardFieldDefaults[role];
                const usesDefaultLabel = !field.label.trim()
                    || field.label.trim() === standardFieldDefaults[currentRole]?.label;
                const ruleState = getDefaultRuleState(role, getStandardFitmentCompatibility(current.fields).compatible);

                return normalizeSimpleFieldSettings({
                    ...field,
                    key: role,
                    label: usesDefaultLabel ? defaults.label : field.label,
                    dataType: "Text" as FitmentFieldDataType,
                    ...ruleState,
                    sortDirection: getDefaultSortDirection(role),
                    isVisible: true,
                    isEnabled: true,
                    maxLength: defaultTextMaxLength,
                });
            });

            return { ...current, catalogType: inferCatalogType(fields), fields };
        });
    }, []);

    const moveActiveField = useCallback((fromIndex: number, toIndex: number) => {
        const fields = activeFieldsRef.current;
        if (fromIndex === toIndex || toIndex < 0 || toIndex >= fields.length) return;
        setSuccess(undefined);
        setReorderMessage(`${fields[fromIndex].field.label || "Field"} moved to position ${toIndex + 1}.`);
        setEditingFieldIndex(current => {
            if (current === undefined) return current;
            if (current === fromIndex) return toIndex;
            if (fromIndex < current && current <= toIndex) return current - 1;
            if (toIndex <= current && current < fromIndex) return current + 1;
            return current;
        });
        setDraft((current) => {
            const active = current?.fields.filter((field) => field.isEnabled) ?? [];
            if (!current || fromIndex === toIndex || toIndex < 0 || toIndex >= active.length) {
                return current;
            }

            const inactive = current.fields.filter((field) => !field.isEnabled);
            const [field] = active.splice(fromIndex, 1);
            active.splice(toIndex, 0, field);

            // Keep disabled fields available, but out of the main selector order.
            const fields = [...active, ...inactive];
            return { ...current, catalogType: inferCatalogType(fields), fields: normalizeDisplayOrder(fields) };
        });
    }, []);

    const addField = useCallback(() => {
        if (!draft || enabledFieldCount >= draft.maxFields) return;
        const field = createFieldFromRole(enabledFieldCount + 1, "custom", standardFitmentCompatibility.compatible);
        // Explicit keys keep a custom label such as "Year" from changing its role.
        let keyIndex = enabledFieldCount + 1;
        const usedKeys = new Set(draft.fields.map(existing => existing.key));
        while (usedKeys.has(`custom_${keyIndex}`)) keyIndex += 1;
        field.key = `custom_${keyIndex}`;
        updateCatalog({ fields: [...draft.fields, field] });
        setEditingFieldIndex(draft.fields.length);
        setError(undefined);
    }, [draft, enabledFieldCount, standardFitmentCompatibility.compatible, updateCatalog]);

    const removeOrDisableField = useCallback((index: number) => {
        setSuccess(undefined);
        setEditingFieldIndex(undefined);
        setDraft((current) => {
            if (!current) return current;

            if (current.fields.length <= 1) return current;
            const fields = current.fields.filter((_, fieldIndex) => fieldIndex !== index);

            const normalizedFields = normalizeDisplayOrder(fields);
            return { ...current, catalogType: inferCatalogType(normalizedFields), fields: normalizedFields };
        });
    }, []);

    const discardChanges = useCallback(() => {
        if (!baseline) return;

        setDraft(cloneCatalog(baseline));
        setError(undefined);
        setSuccess(undefined);
        setEditingFieldIndex(undefined);
        setReorderMessage("");
    }, [baseline]);

    const validate = useCallback(() => {
        if (!draft) return "Fitment catalog is not loaded.";

        const enabledFields = draft.fields.filter((field) => field.isEnabled);
        if (!enabledFields.length) return "At least one field must be enabled.";
        if (enabledFields.some((field) => !field.label.trim())) return "Enabled fields need a label.";

        const labels = enabledFields.map((field) => field.label.trim().toLowerCase());
        if (labels.some((label, index) => labels.indexOf(label) !== index)) {
            return "Enabled field labels must be unique.";
        }

        const standardRoles = enabledFields
            .map((field) => getFieldRole(field))
            .filter((role) => role !== "custom");
        if (standardRoles.some((role, index) => standardRoles.indexOf(role) !== index)) {
            return "Standard fitment field types can each be assigned to only one field.";
        }

        return undefined;
    }, [draft]);

    const saveChanges = useCallback(async () => {
        const validationError = validate();
        if (validationError || !draft) {
            setError(validationError);
            return;
        }

        setSaving(true);
        setError(undefined);
        setSuccess(undefined);

        try {
            const catalogToSave = getActiveCatalog(normalizeSimpleCatalogSettings(draft));
            const saved = getActiveCatalog(normalizeSimpleCatalogSettings(await agent.Settings.updateFitmentCatalog(catalogToSave)));
            setDraft(cloneCatalog(saved));
            setBaseline(cloneCatalog(saved));
            setCachedFitmentCatalogLabel(saved.name);
            setEditingFieldIndex(current => current !== undefined && current < saved.fields.length ? current : undefined);
            setSuccess(section === "referenceData" ? "Fitment data source settings saved." : `${SETTINGS_SECTIONS.selectorFields} saved.`);
        } catch (err: any) {
            setError(err?.response?.data ?? err?.message ?? (section === "referenceData" ? "Failed to save fitment data source settings." : "Failed to save fitment fields."));
        } finally {
            setSaving(false);
        }
    }, [draft, section, validate]);

    useEffect(() => {
        onStatusChange?.({
            draft, baseline, error, reload,
            hasChanges: hasUnsavedChanges,
            loading,
            saving,
            save: saveChanges,
            discard: discardChanges,
        });
    }, [draft, baseline, error, reload, discardChanges, hasUnsavedChanges, loading, onStatusChange, saveChanges, saving]);

    const startFieldDrag = useCallback((event: FieldDragStartEvent, sourceIndex: number) => {
        if (event.button !== 0 || fieldDragRef.current || saving) return;

        const row = fieldRowRefs.current.get(sourceIndex)
            ?? event.currentTarget.closest("[data-fitment-field-row]") as HTMLDivElement | null;
        if (!row) return;

        const ownerDocument = row.ownerDocument;
        const ownerWindow = ownerDocument.defaultView;
        const previousBodyUserSelect = ownerDocument.body.style.userSelect;
        const rect = row.getBoundingClientRect();

        const getDestinationIndex = (clientY: number) => {
            const fields = activeFieldsRef.current;
            let targetSlot = fields.length;

            for (let index = 0; index < fields.length; index += 1) {
                const targetRow = fieldRowRefs.current.get(index);
                if (!targetRow) continue;

                const targetRect = targetRow.getBoundingClientRect();
                if (clientY < targetRect.top + targetRect.height / 2) {
                    targetSlot = index;
                    break;
                }
            }

            return Math.max(0, Math.min(fields.length - 1, targetSlot));
        };

        const updateDrag = (clientX: number, clientY: number) => {
            setFieldDrag((current) => {
                if (!current) return current;

                const next = {
                    ...current,
                    destinationIndex: getDestinationIndex(clientY),
                    pointerX: clientX,
                    pointerY: clientY,
                };
                fieldDragRef.current = next;
                return next;
            });
        };

        const cleanup = () => {
            ownerDocument.removeEventListener("pointermove", handlePointerMove, true);
            ownerDocument.removeEventListener("pointerup", handlePointerUp, true);
            ownerDocument.removeEventListener("pointercancel", handlePointerCancel, true);
            ownerWindow?.removeEventListener("blur", handlePointerCancel, true);
            ownerDocument.body.style.userSelect = previousBodyUserSelect;
            dragCleanupRef.current = undefined;
        };

        const finishDrag = () => {
            const current = fieldDragRef.current;
            cleanup();
            fieldDragRef.current = undefined;
            setFieldDrag(undefined);

            if (current && current.destinationIndex !== current.sourceIndex) {
                moveActiveField(current.sourceIndex, current.destinationIndex);
            }
        };

        const cancelDrag = () => {
            cleanup();
            fieldDragRef.current = undefined;
            setFieldDrag(undefined);
        };

        function handlePointerMove(moveEvent: PointerEvent) {
            moveEvent.preventDefault();
            updateDrag(moveEvent.clientX, moveEvent.clientY);
        }

        function handlePointerUp() {
            finishDrag();
        }

        function handlePointerCancel() {
            cancelDrag();
        }

        event.preventDefault();
        event.stopPropagation();

        ownerDocument.body.style.userSelect = "none";
        ownerDocument.addEventListener("pointermove", handlePointerMove, true);
        ownerDocument.addEventListener("pointerup", handlePointerUp, true);
        ownerDocument.addEventListener("pointercancel", handlePointerCancel, true);
        ownerWindow?.addEventListener("blur", handlePointerCancel, true);

        const dragState = {
            sourceIndex,
            destinationIndex: sourceIndex,
            pointerX: event.clientX,
            pointerY: event.clientY,
            offsetX: event.clientX - rect.left,
            offsetY: event.clientY - rect.top,
            width: rect.width,
            portalContainer: ownerDocument.body,
        };

        dragCleanupRef.current = cleanup;
        fieldDragRef.current = dragState;
        setFieldDrag(dragState);
    }, [moveActiveField, saving]);

    if (!loading && !draft) {
        return <Layout.Section><Banner tone="critical">{error || "Failed to load fitment fields."}</Banner></Layout.Section>;
    }

    if (loading || !draft) {
        return (
            <Layout.Section>
                <Card>
                    <Box padding="400">
                        <SkeletonBodyText lines={8} />
                    </Box>
                </Card>
            </Layout.Section>
        );
    }

    const renderFieldRow = (
        field: FitmentFieldDefinition,
        originalIndex: number,
        activeIndex: number,
        options: {
            isDragging?: boolean;
            isPreview?: boolean;
            onHandleDragStart?: (event: FieldDragStartEvent) => void;
            rowKey?: string;
            setRef?: (node: HTMLDivElement | null) => void;
        } = {}
    ) => {
        const role = getFieldRole(field);
        const label = field.label.trim() || `Field ${activeIndex + 1}`;
        const roleLabel = role === "custom" ? "Custom field" : standardFieldDefaults[role].label;
        const subtitle = [
            roleLabel === label ? "" : roleLabel,
            isRequiredLockedField(field) ? "Core field" : "",
        ].filter(Boolean).join(" · ");
        return (
            <div
                data-fitment-field-row
                key={options.rowKey}
                ref={options.setRef}
                className={`pf-selector-field${editingFieldIndex === originalIndex ? " pf-selector-field--selected" : ""}${options.isDragging ? " pf-selector-field--dragging" : ""}${options.isPreview ? " pf-selector-field--drag-preview" : ""}`}
            >
                <button
                    type="button"
                    className="pf-selector-field__drag"
                    aria-label={`Reorder ${label}`}
                    aria-describedby="fitment-reorder-help"
                    onPointerDown={options.onHandleDragStart}
                    onClick={() => setReordering(true)}
                    onKeyDown={event => {
                        if (event.key === "ArrowUp" || event.key === "ArrowDown") {
                            event.preventDefault();
                            moveActiveField(activeIndex, activeIndex + (event.key === "ArrowUp" ? -1 : 1));
                        }
                    }}
                    disabled={saving}
                ><Icon source={DragHandleIcon} /></button>
                <button
                    type="button"
                    className="pf-selector-field__select"
                    aria-label={`Edit ${label}`}
                    aria-haspopup="dialog"
                    onClick={() => setEditingFieldIndex(originalIndex)}
                    disabled={saving}
                >
                    <span className="pf-selector-field__name"><span>{label}</span>{subtitle && <small>{subtitle}</small>}</span>
                    <span className="pf-selector-field__rule" aria-label={`Fitment data: ${field.isRequired ? "required" : "optional"}`}>{field.isRequired ? "Required" : "Optional"}</span>
                    <span className="pf-selector-field__rule" aria-label={`Search: ${field.isFilterable ? "required" : "optional"}`}>{field.isFilterable ? "Required" : "Optional"}</span>
                    <span className="pf-selector-field__edit">Edit</span>
                </button>
                {reordering && !options.isPreview && <div className="pf-selector-field__move">
                    <Button icon={ArrowUpIcon} variant="tertiary" accessibilityLabel={`Move ${label} up`} disabled={activeIndex === 0 || saving} onClick={() => moveActiveField(activeIndex, activeIndex - 1)} />
                    <Button icon={ArrowDownIcon} variant="tertiary" accessibilityLabel={`Move ${label} down`} disabled={activeIndex === enabledFieldCount - 1 || saving} onClick={() => moveActiveField(activeIndex, activeIndex + 1)} />
                </div>}
            </div>
        );
    };

    const saveBarMarkup = showSaveBar && section === "referenceData" ? (
        <SaveBar id="fitment-fields-save-bar" open={hasUnsavedChanges}>
            <button onClick={discardChanges} disabled={saving}>
                Discard
            </button>
            <button variant="primary" onClick={saveChanges} disabled={saving} loading={saving}>
                Save
            </button>
        </SaveBar>
    ) : null;

    if (section === "referenceData") {
        const standardEnabled = draft.includeVcdbReferenceData ?? false;
        const turn14Connected = !!draft.turn14ReferenceDataAvailable;
        const turn14Enabled = turn14Connected && (draft.includeTurn14ReferenceData ?? false);
        const productLinksEnabled = draft.syncTurn14ProductLinks ?? true;
        const vehicleTagsEnabled = productLinksEnabled && (draft.syncTurn14VehicleTagsToShopify ?? false);

        return (
            <>
                {saveBarMarkup}

                <Layout.Section>
                    <BlockStack gap="400">
                        {error ? <Banner tone="critical">{error}</Banner> : null}
                        {success ? <Banner tone="success">{success}</Banner> : null}

                        <section aria-labelledby="standard-vehicle-source-heading">
                            <Card padding="0">
                                <div className="pf-fitment-source__body">
                                    <div className="pf-fitment-source__header">
                                        <BlockStack gap="200">
                                            <InlineStack gap="200" blockAlign="center">
                                                <Text as="h3" id="standard-vehicle-source-heading" variant="headingSm">Standard vehicle database</Text>
                                                <Badge tone={standardEnabled ? "success" : undefined}>{standardEnabled ? "Enabled" : "Off"}</Badge>
                                            </InlineStack>
                                            <Text as="p" tone="subdued">Add standard vehicle values to your fitment selectors.</Text>
                                        </BlockStack>
                                        <ReferenceSourceSwitch
                                            label="Enable standard vehicle fitment database"
                                            checked={standardEnabled}
                                            disabled={saving}
                                            onChange={(checked) => updateCatalog({ includeVcdbReferenceData: checked })}
                                        />
                                    </div>
                                    <div className="pf-fitment-source__note">
                                        <Icon source={CheckIcon} tone="subdued" />
                                        <Text as="p" variant="bodySm" tone="subdued">Your existing fitment data stays intact. When off, only saved fitment values are used.</Text>
                                    </div>
                                </div>
                                <div className="pf-fitment-source__footer">
                                    <details className="pf-fitment-source__details">
                                        <summary>Which vehicle fields are supported?</summary>
                                        <BlockStack gap="200">
                                            <Text as="p" variant="bodySm" tone="subdued">{standardVehicleSupportedFields}</Text>
                                            <Text as="p" variant="bodySm" tone="subdued">Available values depend on the vehicle and your enabled selector fields.</Text>
                                            <Text as="p" variant="bodySm" tone="subdued">Additional reference fields are supported in admin selectors. Storefront reference lookups currently support year, make, model, and submodel/trim. Other storefront fields use saved fitment records.</Text>
                                        </BlockStack>
                                    </details>
                                </div>
                            </Card>
                        </section>

                        <section aria-labelledby="turn14-source-heading">
                            <Card padding="0">
                                <div className="pf-fitment-source__body">
                                    <div className="pf-fitment-source__header">
                                        <BlockStack gap="200">
                                            <InlineStack gap="200" blockAlign="center">
                                                <Text as="h3" id="turn14-source-heading" variant="headingSm">Turn14 fitment data</Text>
                                                <Badge tone={turn14Enabled ? "success" : undefined}>{!turn14Connected ? "Not connected" : turn14Enabled ? "Enabled" : "Off"}</Badge>
                                            </InlineStack>
                                            <Text as="p" tone="subdued">Match your existing Shopify products to vehicles using Turn14 fitment records.</Text>
                                        </BlockStack>
                                        {turn14Connected && <ReferenceSourceSwitch
                                            label="Enable Turn14 fitment data"
                                            checked={turn14Enabled}
                                            disabled={saving}
                                            onChange={(checked) => updateCatalog({ includeTurn14ReferenceData: checked })}
                                        />}
                                    </div>
                                    <div className="pf-fitment-source__connection">
                                        <InlineStack gap="300" blockAlign="center">
                                            {turn14Connected && (
                                                <div className="pf-fitment-source__connection-status"><Icon source={CheckIcon} tone="success" /><Text as="span" variant="bodySm" tone="subdued">Turn14 connected</Text></div>
                                            )}
                                            <Turn14IntegrationSettings
                                                display="button"
                                                disabled={saving}
                                                fitmentDataEnabled={draft.includeTurn14ReferenceData ?? false}
                                                onSaved={handleTurn14Saved}
                                                autoOpen={autoOpenTurn14}
                                                onAutoOpened={onTurn14AutoOpened}
                                            />
                                        </InlineStack>
                                    </div>
                                    {onOpenFitmentSpreadsheet && <div className="pf-fitment-source__connection">
                                        <InlineStack gap="100" blockAlign="center">
                                            <Text as="span" variant="bodySm">Have a CSV from Data Here-to-There?</Text>
                                            <Button variant="plain" disabled={saving} onClick={onOpenFitmentSpreadsheet}>Import a spreadsheet</Button>
                                        </InlineStack>
                                    </div>}
                                    {turn14Enabled && (
                                        <div className="pf-fitment-source__options">
                                            <BlockStack gap="300">
                                                <Text as="h4" variant="headingSm">Product matching &amp; sync</Text>
                                                <Checkbox
                                                    label="Sync product links"
                                                    helpText={turn14ProductLinkBehavior}
                                                    checked={productLinksEnabled}
                                                    disabled={saving}
                                                    onChange={(checked) => updateCatalog({
                                                        syncTurn14ProductLinks: checked,
                                                        syncTurn14VehicleTagsToShopify: checked
                                                            ? draft.syncTurn14VehicleTagsToShopify
                                                            : false,
                                                    })}
                                                />
                                                <Checkbox
                                                    label="Write vehicle tags to Shopify"
                                                    helpText={productLinksEnabled ? turn14ShopifyTagBehavior : "Turn on product-link sync to write vehicle tags to Shopify."}
                                                    checked={vehicleTagsEnabled}
                                                    disabled={saving || !productLinksEnabled}
                                                    onChange={(checked) => updateCatalog({ syncTurn14VehicleTagsToShopify: checked })}
                                                />
                                                {vehicleTagsEnabled && <div className="pf-fitment-source__tag-options">
                                                    <Checkbox
                                                        label="Skip products that exceed the tag limit"
                                                        helpText={turn14TagLimitBehavior}
                                                        checked={draft.skipShopifyVehicleTagSyncWhenOverLimit ?? true}
                                                        disabled={saving}
                                                        onChange={(checked) => updateCatalog({ skipShopifyVehicleTagSyncWhenOverLimit: checked })}
                                                    />
                                                </div>}
                                            </BlockStack>
                                        </div>
                                    )}
                                </div>
                                <div className="pf-fitment-source__footer">
                                    <Text as="p" variant="bodySm" tone="subdued">{!turn14Connected
                                        ? "Connect to enable fitment matching and configure sync options."
                                        : !turn14Enabled
                                            ? "Turn on Turn14 fitment data to configure product matching and sync."
                                            : "Sync settings apply after you save your changes."}</Text>
                                </div>
                                <Turn14PartnerCallout />
                            </Card>
                        </section>
                        <div className="pf-fitment-source__scope-note">
                            <Icon source={InfoIcon} tone="subdued" />
                            <Text as="p" variant="bodySm" tone="subdued">These sources enrich fitment data. Product imports, inventory, pricing, and order fulfillment are managed separately.</Text>
                        </div>
                    </BlockStack>
                </Layout.Section>
            </>
        );
    }

    return (
        <Layout.Section>
            <div className="pf-selector-fields">
                {showSaveBar && <div className="pf-selector-fields__standalone-actions">
                    {hasUnsavedChanges && <Button onClick={discardChanges} disabled={saving}>Discard</Button>}
                    <Button variant="primary" onClick={saveChanges} disabled={!hasUnsavedChanges} loading={saving}>Save changes</Button>
                </div>}
                {error && <Banner tone="critical">{error}</Banner>}
                {success && <Banner tone="success">{success}</Banner>}
                <fieldset className="pf-selector-fields__fieldset" disabled={saving}>
                    <div className="pf-selector-fields__catalog">
                        <div><TextField label="Catalog name" autoComplete="off" value={draft.name} placeholder="Vehicles" onChange={name => updateCatalog({ name })} /></div>
                        <div className="pf-selector-fields__inline" role="status">
                            <Text as="span" variant="bodySm" tone="subdued">{saving ? "Saving changes…" : hasUnsavedChanges ? "Unsaved changes" : ""}</Text>
                        </div>
                    </div>
                    <div className="pf-selector-fields__workspace">
                        <div className="pf-selector-fields__list-column">
                            <section className={`pf-selector-fields__list${reordering ? " pf-selector-fields__list--reordering" : ""}`} aria-label={SETTINGS_SECTIONS.selectorFields}>
                                <div className="pf-selector-fields__list-heading">
                                    <InlineStack gap="200" blockAlign="center">
                                        <Text as="h2" variant="headingMd">Fields</Text>
                                        <Badge>{`${enabledFieldCount} of ${draft.maxFields} fields`}</Badge>
                                        <SettingsTooltip content={standardFitmentCompatibility.tooltip}>
                                            <Badge tone={standardFitmentCompatibility.tone}>{standardFitmentCompatibility.label}</Badge>
                                        </SettingsTooltip>
                                    </InlineStack>
                                    <Button variant="tertiary" icon={reordering ? CheckIcon : SortIcon} pressed={reordering} onClick={() => setReordering(current => !current)}>{reordering ? "Done reordering" : "Reorder"}</Button>
                                </div>
                                <div className="pf-selector-fields__column-head" aria-hidden="true"><span>Field</span><span>Fitment data</span><span>Search</span><span /></div>
                                {!activeFields.length && <div className="pf-selector-fields__empty"><Text as="p" tone="subdued">Add a field to start your selector.</Text></div>}
                                {displayedActiveFields.map(({ field, originalIndex }, index) => renderFieldRow(field, originalIndex, index, {
                                    isDragging: !!draggedActiveField && originalIndex === draggedActiveField.originalIndex,
                                    onHandleDragStart: fieldDrag ? undefined : event => startFieldDrag(event, index),
                                    rowKey: field.id ? `field-${field.id}` : `new-field-${field.key}`,
                                    setRef: node => { if (node) fieldRowRefs.current.set(index, node); else fieldRowRefs.current.delete(index); },
                                }))}
                                <div className="pf-selector-fields__list-footer">
                                    <Button id="fitment-add-field" icon={PlusIcon} onClick={addField} disabled={!canOpenAddField}>Add field</Button>
                                    {!canOpenAddField && <Text as="span" variant="bodySm" tone="subdued">{`Maximum ${draft.maxFields} fields`}</Text>}
                                </div>
                            </section>
                            <div className="pf-selector-fields__hint"><Text as="p" variant="bodySm" tone="subdued">“Required” shows where a value is needed. Select a field to edit its label, rules, and sorting.</Text></div>
                            <div className="pf-selector-fields__compatibility">
                                <Button
                                    variant="tertiary"
                                    icon={InfoIcon}
                                    disclosure={compatibilityInfoOpen ? "up" : "down"}
                                    ariaExpanded={compatibilityInfoOpen}
                                    ariaControls="fitment-field-compatibility-info"
                                    onClick={() => setCompatibilityInfoOpen(open => !open)}
                                >About field compatibility</Button>
                                <div id="fitment-field-compatibility-info" className="pf-selector-fields__compatibility-content" hidden={!compatibilityInfoOpen}>
                                    <BlockStack gap="200">
                                        <Text as="p" tone="subdued">You can customize these fields for your products, including non-vehicle catalogs, and supply your own matching data.</Text>
                                        <Text as="p" tone="subdued">If you plan to use standard vehicle data, ACES imports, or Turn14 fitment, the first three fields must be Year–Make–Model or Make–Model–Year. Additional fields can follow.</Text>
                                    </BlockStack>
                                </div>
                            </div>
                            <span className="pf-selector-fields__sr-only" id="fitment-reorder-help">Drag to reorder, or use the up and down arrow keys.</span>
                            <span className="pf-selector-fields__sr-only" role="status">{reorderMessage}</span>
                        </div>
                        {selectedField && editingFieldIndex !== undefined && <FitmentFieldEditor
                            key={editingFieldIndex}
                            field={selectedField}
                            role={getFieldRole(selectedField)}
                            roleOptions={getFieldRoleOptions(draft.fields, editingFieldIndex)}
                            requiredLocked={isRequiredLockedField(selectedField)}
                            canRemove={enabledFieldCount > 1}
                            onChange={changes => updateField(editingFieldIndex, changes)}
                            onRoleChange={role => updateFieldRole(editingFieldIndex, role)}
                            onClose={() => setEditingFieldIndex(undefined)}
                            onRemove={() => removeOrDisableField(editingFieldIndex)}
                        />}
                    </div>
                </fieldset>
                {fieldDrag && draggedActiveField ? createPortal(
                    <div className="pf-selector-fields__drag-overlay" aria-hidden="true" style={{
                        left: fieldDrag.pointerX - fieldDrag.offsetX,
                        top: fieldDrag.pointerY - fieldDrag.offsetY,
                        width: fieldDrag.width,
                    }}>{renderFieldRow(draggedActiveField.field, draggedActiveField.originalIndex, fieldDrag.sourceIndex, { isPreview: true })}</div>,
                    fieldDrag.portalContainer
                ) : null}
            </div>
        </Layout.Section>
    );
};

export default FitmentFieldsSettings;
